/**
 * Entity-type-aware pricing constants.
 *
 * Different entity types (performer, venue) have different price points
 * for the same plan tier. Both annual and monthly billing are supported.
 */

// The `.js` extension is deliberate, and the only one in this package — the
// backend's `tests/unit/pricing.test.ts` pulls this module into the root tsc
// program, which runs `moduleResolution: nodenext` and rejects extensionless
// relative imports (TS2835). `bundler` resolution accepts the extension too,
// and the import is type-only so it is erased before Vite ever sees it.
import type { PlanType } from '../types/PlanFeatures.js';

export type EntityType = 'venue' | 'performer';
export type BillingInterval = 'monthly' | 'annual';

export interface PlanPricing {
    /** Monthly price in dollars */
    monthly: number;
    /**
     * Annual price per month in dollars, for headline display only.
     * This is a ROUNDED presentation of `annualTotal / 12` — never multiply it
     * by 12 to quote a yearly charge. Use `annualTotal`.
     */
    annual: number;
    /**
     * The actual amount charged once per year, in dollars. This must match the
     * amount on the annual Stripe Price named by `stripePriceEnvAnnual`.
     *
     * It is stored explicitly rather than derived because the Stripe prices are
     * rounded to friendly numbers ($70, $290, $790), so `annual * 12` disagrees
     * with the real charge — and in two of four cases it disagreed *downwards*,
     * quoting the customer less than they were about to be billed.
     */
    annualTotal: number;
    /** Env var name for monthly Stripe Price ID */
    stripePriceEnvMonthly: string;
    /** Env var name for annual Stripe Price ID */
    stripePriceEnvAnnual: string;
}

/**
 * Pricing matrix: entity type → plan → pricing info.
 * null means the plan is free or not purchasable (dev).
 */
export const PLAN_PRICING: Record<EntityType, Partial<Record<PlanType, PlanPricing | null>>> = {
    performer: {
        free: null,
        pro: {
            // $7/mo or $70/yr ($5.83/mo billed annually, displayed as $6)
            monthly: 7,
            annual: 6,
            annualTotal: 70,
            stripePriceEnvMonthly: 'STRIPE_PERFORMER_PRO_MONTHLY',
            stripePriceEnvAnnual: 'STRIPE_PERFORMER_PRO_ANNUAL',
        },
        business: {
            // $29/mo or $290/yr ($24.17/mo billed annually, displayed as $24)
            monthly: 29,
            annual: 24,
            annualTotal: 290,
            stripePriceEnvMonthly: 'STRIPE_PERFORMER_BIZ_MONTHLY',
            stripePriceEnvAnnual: 'STRIPE_PERFORMER_BIZ_ANNUAL',
        },
        dev: null,
    },
    venue: {
        free: null,
        pro: {
            // $12/mo or $120/yr ($10/mo billed annually)
            monthly: 12,
            annual: 10,
            annualTotal: 120,
            stripePriceEnvMonthly: 'STRIPE_VENUE_PRO_MONTHLY',
            stripePriceEnvAnnual: 'STRIPE_VENUE_PRO_ANNUAL',
        },
        business: {
            // $79/mo or $790/yr ($65.83/mo billed annually, displayed as $65)
            monthly: 79,
            annual: 65,
            annualTotal: 790,
            stripePriceEnvMonthly: 'STRIPE_VENUE_BIZ_MONTHLY',
            stripePriceEnvAnnual: 'STRIPE_VENUE_BIZ_ANNUAL',
        },
        dev: null,
    },
};

/**
 * Get the display price for a plan based on entity type and billing interval.
 * Returns null for free/dev plans.
 */
export function getPlanPrice(
    entityType: EntityType,
    plan: PlanType,
    interval: BillingInterval = 'monthly'
): number | null {
    const pricing = PLAN_PRICING[entityType]?.[plan];
    if (!pricing) return null;
    return pricing[interval];
}

/**
 * Get the amount actually charged for a year of this plan, in dollars.
 * Returns null for free/dev plans.
 *
 * Always use this to quote a yearly total — `getPlanPrice(..., 'annual') * 12`
 * is a per-month display figure and does not match the Stripe charge.
 */
export function getPlanAnnualTotal(
    entityType: EntityType,
    plan: PlanType
): number | null {
    const pricing = PLAN_PRICING[entityType]?.[plan];
    if (!pricing) return null;
    return pricing.annualTotal;
}

/**
 * Get the annual savings percentage compared to monthly billing.
 *
 * Derived from the two amounts a customer is actually charged — `annualTotal`
 * against twelve monthly payments — NOT from the rounded per-month display
 * figures. Those round in both directions, so deriving from them both
 * understated the saving (Performer Pro read 14% against a real 17%) and
 * overstated it (Venue Business read 18% against a real 17%). A savings claim
 * that overstates is the one that gets you in trouble.
 *
 * Every plan currently prices annual at ten months for twelve, so all four
 * return 17%. That is a pricing convention, not a guarantee — the arithmetic
 * here stays general.
 */
export function getAnnualSavingsPercent(
    entityType: EntityType,
    plan: PlanType
): number {
    const pricing = PLAN_PRICING[entityType]?.[plan];
    if (!pricing) return 0;
    return Math.round((1 - pricing.annualTotal / (pricing.monthly * 12)) * 100);
}
